import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, MessageCircle, MapPin, Clock, ArrowRight, Phone } from "lucide-react";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { useSite } from "@/context/SiteContext";
import { WHATSAPP_DISPLAY, PHONE_DISPLAY, PHONE_TEL } from "@/i18n/translations";

const field = "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 font-sans text-sm text-charcoal focus:outline-none focus:border-navy focus:ring-2 focus:ring-champagne/40 transition-colors";
const labelCls = "block text-xs font-sans tracking-wide uppercase text-navy/60 mb-2";

const SMS_INFO_TEXT = "I agree to receive non-marketing SMS messages from REVIVE Regenerative Medical Clinic regarding appointment confirmations, appointment reminders, scheduling and rescheduling, and consultation follow-ups. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase. Reply STOP to opt out or HELP for assistance.";
const SMS_MARKETING_TEXT = "I agree to receive marketing and promotional SMS messages from REVIVE Regenerative Medical Clinic regarding services, updates, offers, and promotions. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase. Reply STOP to opt out or HELP for assistance.";
const SMS_INFO_TEXT_ES = "Acepto recibir mensajes SMS no promocionales de REVIVE Regenerative Medical Clinic relacionados con confirmaciones de citas, recordatorios de citas, programación y reprogramación, y seguimientos de consulta. La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es una condición de compra. Responda STOP para cancelar o HELP para obtener ayuda.";
const SMS_MARKETING_TEXT_ES = "Acepto recibir mensajes SMS de marketing y promocionales de REVIVE Regenerative Medical Clinic sobre servicios, novedades, ofertas y promociones. La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es una condición de compra. Responda STOP para cancelar o HELP para obtener ayuda.";

const ALTERNATES = {
  en: "https://revivestem.com.mx/contact",
  es: "https://revivestem.com.mx/es/contacto",
  "x-default": "https://revivestem.com.mx/contact",
};

const empty = { first_name: "", last_name: "", email: "", phone: "", message: "" };

const Contact = ({ es = false }) => {
  const { lang, langTick, changeLang, openWhatsApp, openBooking, API, track } = useSite();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [dialCode, setDialCode] = useState("+1");
  const [smsInfo, setSmsInfo] = useState(false);
  const [smsMarketing, setSmsMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true }); }, []);

  // Language switcher keeps the visitor on the equivalent contact page.
  // Only reacts to an explicit toggle click AFTER mount (langTick) — a stored
  // preference never redirects a direct visit (crawlers/shared links see this page).
  const prevLang = React.useRef(langTick);
  useEffect(() => {
    if (prevLang.current === langTick) return;
    prevLang.current = langTick;
    navigate(es ? "/contact" : "/es/contacto", { replace: true });
  }, [lang, langTick, es, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error(es ? "Por favor completa los campos requeridos." : "Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const rawPhone = form.phone.trim();
      const combinedPhone = rawPhone.startsWith("+") ? rawPhone : `${dialCode}${rawPhone}`;
      await axios.post(`${API}/bookings`, {
        consultation_type: "General Contact",
        first_name: form.first_name, last_name: form.last_name, email: form.email, phone: combinedPhone,
        country: "", preferred_contact: (smsInfo || smsMarketing) ? "SMS" : "Email",
        reason: form.message, message: form.message,
        treatment_interest: "General Inquiry", source: "Website Contact",
        sms_consent: smsInfo, sms_marketing_consent: smsMarketing, consent: true, language: es ? "es" : "en",
      });
      track("contact_form_submit", { sms_info: smsInfo, sms_marketing: smsMarketing });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const apiMsg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail;
      toast.error(apiMsg || (es ? "Algo salió mal. Inténtalo por WhatsApp." : "Something went wrong. Please try WhatsApp."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-offwhite min-h-screen pb-16 md:pb-0" data-testid={es ? "contacto-page" : "contact-page"}>
      <Seo
        title={es ? "Contáctanos | REVIVE Regenerative Medical Clinic" : "Contact Us"}
        description={es
          ? "Contacta a REVIVE Regenerative Medical Clinic en Tijuana. Envíanos un mensaje, acepta opcionalmente recibir SMS, o escríbenos por WhatsApp."
          : "Contact REVIVE Regenerative Medical Clinic in Tijuana. Send a message, optionally opt in to SMS updates, or reach us on WhatsApp at +1 (555) 303-9140."}
        path={es ? "/es/contacto" : "/contact"}
        alternates={ALTERNATES}
      />
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-navy/5">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Revive" className="h-9 w-auto" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl text-gold">Revive</span>
              <span className="font-sans text-[9px] tracking-[0.22em] uppercase text-navy/55 mt-1">Regenerative Medical Clinic</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-navy/15 overflow-hidden" data-testid="contact-lang-toggle">
              <button onClick={() => changeLang("en")} className={`px-3.5 py-1.5 text-xs font-sans tracking-wide transition-colors ${!es ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`} data-testid="contact-lang-en">EN</button>
              <button onClick={() => changeLang("es")} className={`px-3.5 py-1.5 text-xs font-sans tracking-wide transition-colors ${es ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`} data-testid="contact-lang-es">ES</button>
            </div>
            <button onClick={() => openBooking()} className="rounded-full bg-navy text-white text-sm px-5 py-2.5 hover:bg-royal transition-colors" data-testid="contact-book">
              {es ? "Solicitar una consulta" : "Request a Consultation"}
            </button>
          </div>
        </div>
      </header>

      <div className="bg-navy text-white">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 pt-16 pb-14">
          <Link to="/" className="inline-flex items-center gap-2 text-champagne/80 hover:text-champagne text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> {es ? "Volver al inicio" : "Back to home"}
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{es ? "Contáctanos" : "Contact Us"}</h1>
          <p className="font-sans text-white/60 mt-4 max-w-2xl">
            {es
              ? "Envíanos un mensaje y un miembro de nuestro equipo de atención al paciente te contactará. También puedes escribirnos por WhatsApp."
              : "Send us a message and a member of our patient care team will be in touch. You can also reach us on WhatsApp."}
          </p>
        </div>
      </div>

      <main className="max-w-[1100px] mx-auto px-6 sm:px-10 py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {done ? (
            <div className="rounded-3xl bg-white border border-navy/5 p-10 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-champagne/30 text-navy grid place-items-center mx-auto mb-6"><Check size={30} /></div>
              <h2 className="font-serif text-3xl text-navy">{es ? "Gracias por contactar a Revive." : "Thank you for contacting Revive."}</h2>
              <p className="font-sans text-charcoal/65 mt-3">{es ? "Un miembro de nuestro equipo te contactará pronto." : "A member of our patient care team will contact you shortly."}</p>
              <button onClick={() => openWhatsApp()} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 font-medium hover:brightness-110 transition-[filter]" data-testid="contact-success-wa">
                <MessageCircle size={18} /> {es ? "Chatea con nosotros ahora" : "Chat With Us Now"}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-white border border-navy/5 p-7 sm:p-9 shadow-[0_8px_30px_rgba(15,37,55,0.06)]" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>{es ? "Nombre" : "First Name"} *</label><input className={field} value={form.first_name} onChange={set("first_name")} data-testid="contact-first" /></div>
                <div><label className={labelCls}>{es ? "Apellido" : "Last Name"} *</label><input className={field} value={form.last_name} onChange={set("last_name")} data-testid="contact-last" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>{es ? "Correo Electrónico" : "Email"} *</label><input type="email" className={field} value={form.email} onChange={set("email")} data-testid="contact-email" /></div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>{es ? "Teléfono Móvil" : "Mobile Phone Number"} *</label>
                  <div className="space-y-2">
                    <select value={dialCode} onChange={(e) => setDialCode(e.target.value)} className={`${field} w-40 cursor-pointer`} data-testid="contact-phone-country" aria-label="Country dialing code">
                      <option value="+1">US/CA +1</option>
                      <option value="+52">MX +52</option>
                    </select>
                    <input className={field} value={form.phone} onChange={set("phone")} data-testid="contact-phone" placeholder={dialCode === "+52" ? "664 123 4567" : "951-631-9209"} />
                  </div>
                </div>
                <div className="sm:col-span-2 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-navy/10 bg-offwhite p-4" data-testid="contact-sms-info-label">
                    <input type="checkbox" checked={smsInfo} onChange={(e) => setSmsInfo(e.target.checked)} className="mt-0.5 w-4 h-4 accent-navy shrink-0" data-testid="contact-sms-info" />
                    <span className="font-sans text-[11px] leading-relaxed text-charcoal/70">{es ? SMS_INFO_TEXT_ES : SMS_INFO_TEXT}</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-navy/10 bg-offwhite p-4" data-testid="contact-sms-marketing-label">
                    <input type="checkbox" checked={smsMarketing} onChange={(e) => setSmsMarketing(e.target.checked)} className="mt-0.5 w-4 h-4 accent-navy shrink-0" data-testid="contact-sms-marketing" />
                    <span className="font-sans text-[11px] leading-relaxed text-charcoal/70">{es ? SMS_MARKETING_TEXT_ES : SMS_MARKETING_TEXT}</span>
                  </label>
                  <p className="font-sans text-[11px] leading-relaxed text-charcoal/55 px-1" data-testid="contact-acknowledgement">
                    {es ? (
                      <>Al enviar este formulario, reconoces nuestra{" "}
                        <Link to="/es/privacidad" className="underline hover:text-navy">Política de Privacidad</Link>{" "}y{" "}
                        <Link to="/es/terminos" className="underline hover:text-navy">Términos de Uso</Link>.</>
                    ) : (
                      <>By submitting this form, you acknowledge our{" "}
                        <Link to="/privacy" className="underline hover:text-navy">Privacy Policy</Link>{" "}and{" "}
                        <Link to="/terms" className="underline hover:text-navy">Terms of Use</Link>.</>
                    )}
                  </p>
                </div>
                <div className="sm:col-span-2"><label className={labelCls}>{es ? "Mensaje" : "Message"}</label><textarea rows={4} className={field} value={form.message} onChange={set("message")} data-testid="contact-message" /></div>
              </div>
              <button type="submit" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-8 py-4 font-medium hover:bg-royal transition-colors disabled:opacity-60" data-testid="contact-submit">
                {loading ? (es ? "Enviando…" : "Sending…") : (es ? "Enviar Mensaje" : "Send Message")}
                {!loading && <ArrowRight size={17} />}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl bg-navy text-white p-7">
            <h3 className="font-serif text-2xl">{es ? "Clínica Revive" : "Revive Clinic"}</h3>
            <div className="mt-5 space-y-4 text-sm">
              <div className="flex items-start gap-3"><MapPin className="text-champagne shrink-0 mt-0.5" size={18} /><span className="font-sans text-white/80">Central Torreo · Av. Santa María, Calete, Int. 402<br />Tijuana, Baja California, México</span></div>
              <div className="flex items-start gap-3"><Clock className="text-champagne shrink-0 mt-0.5" size={18} /><span className="font-sans text-white/80">{es ? "Lunes – Sábado · 9:00 AM – 5:00 PM" : "Monday – Saturday · 9:00 AM – 5:00 PM"}</span></div>
              <div className="flex items-start gap-3"><Phone className="text-champagne shrink-0 mt-0.5" size={18} /><a href={PHONE_TEL} className="font-sans text-white/80 hover:text-champagne transition-colors" data-testid="contact-phone-link">{PHONE_DISPLAY}</a></div>
              <div className="flex items-start gap-3"><MessageCircle className="text-champagne shrink-0 mt-0.5" size={18} /><a href="https://wa.me/15553039140" target="_blank" rel="noreferrer" className="font-sans text-white/80 hover:text-champagne transition-colors">{WHATSAPP_DISPLAY}</a></div>
            </div>
            <button onClick={() => openWhatsApp()} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 font-medium hover:brightness-110 transition-[filter]" data-testid="contact-wa">
              <MessageCircle size={18} /> {es ? "Mensaje por WhatsApp" : "Message on WhatsApp"}
            </button>
          </div>
          <div className="overflow-hidden rounded-3xl border border-navy/10 h-[260px]">
            <iframe title="Revive location — Tijuana" src="https://www.google.com/maps?q=Av.%20Santa%20Mar%C3%ADa%2C%20Tijuana%2C%20Baja%20California%2C%20Mexico&output=embed" className="w-full h-full grayscale contrast-125" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </aside>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
